
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Facebook, Menu, X, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { contactInfo, menu, assets } from '../data';
import SharedBottomSections from './SharedBottomSections';

export default function Layout({ children }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [activeMobileCategory, setActiveMobileCategory] = useState(null);
  const location = useLocation();

  const closeMenu = () => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
    setMobileExpanded(null);
    setActiveMobileCategory(null);
  };

  const toggleMobileExpanded = (label) => {
    setMobileExpanded(mobileExpanded === label ? null : label);
    setActiveMobileCategory(null);
  };

  return (
    <div className="min-h-screen flex flex-col font-sans text-slate-900">
      {/* Top Bar removed to match Lykkebo design */}

      {/* Header / Navigation */}
      <header className="bg-[hsl(var(--background))] sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex justify-between items-center h-18 sm:h-22 xl:h-24 relative">
            {/* Logo */}
            <div className="flex items-center justify-start">
              <Link to="/" className="flex-shrink-0" onClick={closeMenu}>
                <img src={assets.logo} alt="Syddansk Malerfirma" className="h-10 sm:h-12 md:h-14 lg:h-18 max-w-[180px] sm:max-w-none w-auto object-contain transition-transform hover:scale-[1.02]" />
              </Link>
            </div>

            {/* Desktop Menu */}
            <nav className="hidden xl:flex space-x-8">
              {menu.map((item) => (
                <div 
                  key={item.label} 
                  className="relative group py-2"
                  onMouseEnter={() => {
                    if (item.children || item.categories) {
                      setActiveDropdown(item.label);
                    }
                  }}
                  onMouseLeave={() => {
                    if (item.children || item.categories) {
                      setActiveDropdown(null);
                    }
                  }}
                >
                  {item.children || item.categories ? (
                    <button
                      className={`flex items-center text-base font-semibold transition-colors hover:text-teal-900 ${
                        location.pathname.startsWith(item.href) ? 'text-teal-900' : 'text-slate-800'
                      }`}
                    >
                      {item.label}
                      <ChevronDown size={16} className="ml-1 transition-transform duration-200 group-hover:rotate-180" />
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      className={`text-base font-semibold transition-colors hover:text-teal-900 ${
                        location.pathname === item.href ? 'text-teal-900' : 'text-slate-800'
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}

                  {/* Standard Dropdown */}
                  {item.children && (
                    <div 
                      className={`absolute left-0 mt-4 w-64 bg-white border border-stone-200 shadow-xl rounded-xl overflow-hidden transition-all duration-200 ${
                        activeDropdown === item.label ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                      }`}
                    >
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.href}
                          className="block px-5 py-3 text-sm font-medium text-slate-700 hover:bg-stone-100 hover:text-teal-900 transition-colors"
                          onClick={() => setActiveDropdown(null)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}

                  {/* Mega Menu Dropdown */}
                  {item.categories && (
                    <div 
                      className={`absolute left-1/2 -translate-x-1/2 mt-4 w-[920px] bg-white border border-stone-200 shadow-2xl rounded-2xl overflow-hidden transition-all duration-200 p-8 grid grid-cols-3 gap-8 ${
                        activeDropdown === item.label ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                      }`}
                    >
                      {item.categories.map((category) => (
                        <div key={category.title} className="space-y-4">
                          <h4 className="text-sm font-bold tracking-wider text-[#0b4e47] border-b border-stone-100 pb-2">
                            {category.title}
                          </h4>
                          <ul className="space-y-2">
                            {category.items.map((subItem) => (
                              <li key={subItem.label}>
                                <Link
                                  to={subItem.href}
                                  className="block text-sm text-slate-600 hover:text-teal-900 hover:translate-x-1 transition-all duration-150"
                                  onClick={() => setActiveDropdown(null)}
                                >
                                  {subItem.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden xl:block">
              <Link
                to="/kontakt"
                className="bg-[#ff6a00] hover:bg-[#e05d00] text-white px-8 py-3.5 rounded-full text-sm font-bold transition-all shadow-lg hover:shadow-orange-500/25 active:scale-95 uppercase tracking-wider"
              >
                Få et uforpligtende tilbud
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="xl:hidden flex items-center absolute right-0 top-1/2 -translate-y-1/2">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-slate-800 hover:text-teal-900 transition-colors"
              >
                {isMenuOpen ? <X size={32} /> : <Menu size={32} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`xl:hidden fixed inset-0 z-40 bg-white transition-all duration-500 ease-in-out transform ${
            isMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
          }`}
          style={{ top: '96px', height: 'calc(100vh - 96px)' }}
        >
          <div className="flex flex-col h-full overflow-y-auto bg-stone-100/30">
            <div className="px-6 py-8 space-y-2">
              {menu.map((item) => (
                <div key={item.label} className="border-b border-slate-100 last:border-0 pb-2 mb-2">
                  {item.children || item.categories ? (
                    <>
                      <button
                        onClick={() => toggleMobileExpanded(item.label)}
                        className="flex items-center justify-between w-full px-2 py-4 text-xl font-bold text-slate-900 hover:text-teal-900 transition-colors"
                      >
                        <span>{item.label}</span>
                        <ChevronDown 
                          size={24} 
                          className={`transition-transform duration-300 ${mobileExpanded === item.label ? 'rotate-180 text-teal-900' : 'text-slate-400'}`} 
                        />
                      </button>
                      <div 
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                          mobileExpanded === item.label ? 'max-h-[1500px] opacity-100 mt-2 mb-4' : 'max-h-0 opacity-0'
                        }`}
                      >
                        {item.children && (
                          <div className="grid grid-cols-1 gap-1 pl-4 border-l-2 border-teal-900/20 ml-2">
                            {item.children.map((child) => (
                              <Link
                                key={child.label}
                                to={child.href}
                                className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                                  location.pathname === child.href 
                                    ? 'bg-teal-900/10 text-teal-900' 
                                    : 'text-slate-600 hover:bg-slate-100'
                                }`}
                                onClick={closeMenu}
                              >
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        )}

                        {item.categories && (
                          <div className="space-y-4 pl-4 border-l-2 border-teal-900/20 ml-2">
                            {activeMobileCategory === null ? (
                              <div className="space-y-2">
                                {item.categories.map((category) => (
                                  <button
                                    key={category.title}
                                    onClick={() => setActiveMobileCategory(category.title)}
                                    className="flex items-center justify-between w-full px-4 py-3 text-base font-bold tracking-wider text-[#0b4e47] bg-teal-900/5 hover:bg-teal-900/10 rounded-xl transition-all text-left cursor-pointer"
                                  >
                                    <span>{category.title}</span>
                                    <ChevronRight size={18} className="text-teal-900" />
                                  </button>
                                ))}
                              </div>
                            ) : (
                              (() => {
                                const category = item.categories.find(c => c.title === activeMobileCategory);
                                if (!category) return null;
                                return (
                                  <div className="space-y-4">
                                    <button
                                      onClick={() => setActiveMobileCategory(null)}
                                      className="flex items-center text-sm font-semibold text-slate-500 hover:text-teal-900 mb-2 transition-colors cursor-pointer"
                                    >
                                      <ChevronLeft size={16} className="mr-1" />
                                      Tilbage til oversigten
                                    </button>
                                    
                                    <div className="space-y-2">
                                      <h4 className="text-sm font-bold tracking-wider text-[#0b4e47] px-2 py-1 bg-teal-900/5 rounded-lg">
                                        {category.title}
                                      </h4>
                                      <div className="grid grid-cols-1 gap-1">
                                        {category.items.map((subItem) => (
                                          <Link
                                            key={subItem.label}
                                            to={subItem.href}
                                            className={`block px-4 py-2.5 text-base font-medium rounded-lg transition-colors ${
                                              location.pathname === subItem.href 
                                                ? 'bg-teal-900/10 text-teal-900' 
                                                : 'text-slate-600 hover:bg-slate-100'
                                            }`}
                                            onClick={closeMenu}
                                          >
                                            {subItem.label}
                                          </Link>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className={`block px-2 py-4 text-xl font-bold transition-colors ${
                        location.pathname === item.href ? 'text-teal-900' : 'text-slate-900 hover:text-teal-900'
                      }`}
                      onClick={closeMenu}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-auto p-6 space-y-6 bg-white border-t border-stone-200">
              <Link
                to="/kontakt"
                onClick={closeMenu}
                className="flex items-center justify-center w-full bg-[#ff6a00] hover:bg-[#e05d00] text-white py-5 rounded-2xl font-bold text-lg shadow-xl transition-all active:scale-[0.98] uppercase tracking-wider"
              >
                Få et uforpligtende tilbud
              </Link>
              
              <div className="grid grid-cols-2 gap-4">
                <a 
                  href={`tel:${contactInfo.phone}`}
                  className="flex flex-col items-center justify-center p-4 rounded-xl bg-stone-100 border border-stone-200 text-slate-900 hover:bg-stone-200 transition-colors"
                >
                  <Phone size={20} className="text-teal-900 mb-2" />
                  <span className="text-xs font-bold uppercase tracking-wider text-teal-900">Ring til os</span>
                  <span className="text-sm font-bold">{contactInfo.phone}</span>
                </a>
                <a 
                  href={`mailto:${contactInfo.email}`}
                  className="flex flex-col items-center justify-center p-4 rounded-xl bg-stone-100 border border-stone-200 text-slate-900 hover:bg-stone-200 transition-colors"
                >
                  <Mail size={20} className="text-teal-900 mb-2" />
                  <span className="text-xs font-bold uppercase tracking-wider text-teal-900">Skriv til os</span>
                  <span className="text-sm font-bold truncate w-full text-center">Email</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Wrapper to prevent horizontal scroll */}
      <div className="flex-grow overflow-x-hidden flex flex-col">
        {/* Main Content */}
        <main className="flex-grow">
          {children}
        </main>

        {/* Shared Bottom Sections */}
        <SharedBottomSections />
      </div>

      {/* Footer (skjult på /kontakt for at undgå gentagelser, og mere kompakt på mobil) */}
      {location.pathname !== '/kontakt' && (
        <footer className="bg-teal-950 text-teal-100/90 pt-8 sm:pt-14 lg:pt-18 pb-8 sm:pb-12 border-t border-teal-900">
          <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 md:gap-12 mb-8 sm:mb-14 text-center">
              
              {/* Location */}
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full border border-teal-800 bg-teal-900/30 flex items-center justify-center mb-2.5 sm:mb-4 text-orange-400">
                  <svg className="w-5 h-5 sm:w-7 sm:h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                </div>
                <h4 className="text-base sm:text-xl font-bold mb-1 sm:mb-3 text-white">Adresse</h4>
                <p className="text-teal-100/80 text-sm sm:text-base">{contactInfo.address}</p>
                <p className="text-teal-100/80 text-xs sm:text-sm mt-0.5 sm:mt-1">CVR: {contactInfo.cvr}</p>
              </div>

              {/* Contact Info */}
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full border border-teal-800 bg-teal-900/30 flex items-center justify-center mb-2.5 sm:mb-4 text-orange-400">
                  <Phone className="w-5 h-5 sm:w-7 sm:h-7" />
                </div>
                <h4 className="text-base sm:text-xl font-bold mb-1 sm:mb-3 text-white">Kontakt</h4>
                <a href={`tel:${contactInfo.phone}`} className="text-orange-400 font-bold hover:text-orange-300 transition-colors text-base sm:text-lg block mb-1">
                  {contactInfo.phone}
                </a>
                <a href={`mailto:${contactInfo.email}`} className="text-teal-200 hover:text-white transition-colors text-xs sm:text-sm">
                  {contactInfo.email}
                </a>
              </div>

              {/* Opening Hours */}
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full border border-teal-800 bg-teal-900/30 flex items-center justify-center mb-2.5 sm:mb-4 text-orange-400">
                  <svg className="w-5 h-5 sm:w-7 sm:h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                </div>
                <h4 className="text-base sm:text-xl font-bold mb-1 sm:mb-3 text-white">Åbningstider</h4>
                <ul className="space-y-1 text-teal-100/80 text-xs sm:text-sm text-center">
                  <li>Man - Tor: {contactInfo.hours.mon_thu}</li>
                  <li>Fredag: {contactInfo.hours.fri}</li>
                  <li>Lør - Søn: {contactInfo.hours.sat_sun}</li>
                </ul>
              </div>

            </div>

            <div className="border-t border-teal-900 pt-6 sm:pt-8 flex flex-col items-center">
              <div className="flex space-x-6 mb-4 sm:mb-6">
                <a href={contactInfo.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-teal-900 text-teal-100 flex items-center justify-center hover:bg-orange-500 hover:text-white transition-colors shadow-sm">
                  <Facebook size={20} className="sm:w-6 sm:h-6" />
                </a>
              </div>
              <div className="flex justify-center gap-6 mb-3 sm:mb-4 text-xs sm:text-sm">
                <Link to="/privatliv" className="text-teal-300/60 hover:text-orange-400 transition-colors underline underline-offset-4">
                  Privatlivspolitik
                </Link>
              </div>
              <p className="text-teal-300/50 text-xs text-center leading-relaxed">
                © {new Date().getFullYear()} Syddansk Malerfirma.
                <span className="block sm:inline sm:ml-1 text-teal-300/40">Alle rettigheder forbeholdes.</span>
              </p>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
